/**
 * Canonical pipeline-stage groupings for the dashboard.
 * Single source of truth: stat cards and the pipeline chart both classify
 * raw `project.stage` codes through these groups, so the two views can never
 * drift apart.
 */

export const STAGE_GROUPS = {
  tendering: ['itt_received', 'tendering', 'tender_pending_review', 'internal_review', 'enquiry_received', 'tender_in_progress', 'client_review'],
  submitted: ['cleared_for_submission', 'tender_submitted', 'ready_to_submit', 'submitted'],
  preStart: ['pre_start', 'pre_contract'],
  onSite: ['on_site'],
  finalAccount: ['practical_completion', 'final_account'],
  retention: ['defects_liability', 'retention_period'],
  onHold: ['on_hold'],
} as const;

export type StageGroupKey = keyof typeof STAGE_GROUPS;

function setOf(...keys: StageGroupKey[]): Set<string> {
  return new Set(keys.flatMap((k) => STAGE_GROUPS[k] as readonly string[]));
}

/** Stat-card buckets — broader aggregations of the stage groups above. */
export const TENDER_IN_PROGRESS_STAGES = setOf('tendering');
export const TENDER_SUBMITTED_STAGES = setOf('submitted');
/** Anything past tendering/submission that is still a running job. */
export const LIVE_STAGES = setOf('preStart', 'onSite', 'finalAccount', 'retention');

/** Ordered buckets for the pipeline-by-stage chart. */
export interface PipelineBucket {
  key: StageGroupKey;
  label: string;
  color: string;
}

export const PIPELINE_BUCKETS: PipelineBucket[] = [
  { key: 'tendering', label: 'Tendering', color: 'var(--status-warning)' },
  { key: 'submitted', label: 'Submitted', color: 'var(--status-info)' },
  { key: 'preStart', label: 'Pre-start', color: '#8b5cf6' },
  { key: 'onSite', label: 'On site', color: 'var(--status-success)' },
  { key: 'finalAccount', label: 'Final account', color: 'var(--brand)' },
  { key: 'retention', label: 'Retention', color: '#06b6d4' },
  { key: 'onHold', label: 'On hold', color: 'var(--muted-foreground)' },
];

const GROUP_SETS = Object.fromEntries(
  (Object.keys(STAGE_GROUPS) as StageGroupKey[]).map((k) => [k, new Set<string>(STAGE_GROUPS[k])]),
) as Record<StageGroupKey, Set<string>>;

export function stageInGroup(stage: string, group: StageGroupKey): boolean {
  return GROUP_SETS[group].has(stage);
}
